import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'reactstrap';
import { clientsApi, scopesApi } from '../../services/api';
import ClientModal from './ClientModal';
import SecretModal from './SecretModal';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [allScopes, setAllScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [clientModal, setClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const [secretModal, setSecretModal] = useState(false);
  const [secretInfo, setSecretInfo] = useState({ name: '', secret: '' });

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, s] = await Promise.all([clientsApi.getAll(), scopesApi.getAll()]);
      setClients(c);
      setAllScopes(s);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditingClient(null);
    setClientModal(true);
  }

  function openEdit(client) {
    setEditingClient(client);
    setClientModal(true);
  }

  async function handleSave(dto) {
    if (editingClient) {
      await clientsApi.update(editingClient.id, dto);
      await load();
    } else {
      const created = await clientsApi.create(dto);
      await load();
      if (created.clientSecret) {
        setSecretInfo({ name: created.name, secret: created.clientSecret });
        setSecretModal(true);
      }
    }
  }

  async function handleDelete(id) {
    try {
      await clientsApi.delete(id);
      setDeleteConfirm(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRotateSecret(client) {
    try {
      const { client_secret } = await clientsApi.rotateSecret(client.id);
      setSecretInfo({ name: client.name, secret: client_secret });
      setSecretModal(true);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleToggleActive(client) {
    try {
      await clientsApi.toggleActive(client.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  function formatDate(iso) {
    return new Date(iso).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }

  return (
      <div>
        <div className="page-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="page-title">Clients</h1>
            <p className="page-subtitle">Manage OAuth2 client applications</p>
          </div>
          <Button className="btn-primary" onClick={openCreate}>
            + New Client
          </Button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="data-card">
          {loading ? (
              <div className="loading-state">Loading clients...</div>
          ) : clients.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">&#128193;</div>
                <div>No clients yet.</div>
                <Button className="btn-primary mt-3" onClick={openCreate}>
                  Create your first client
                </Button>
              </div>
          ) : (
              <table className="table admin-table">
                <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Client ID</th>
                  <th>Scopes</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {clients.map((c) => (
                    <tr key={c.id}>
                      <td style={{ color: '#8892a4', fontSize: 12 }}>#{c.id}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        {c.description && (
                            <div style={{ fontSize: 12, color: '#8892a4' }}>{c.description}</div>
                        )}
                      </td>
                      <td><span className="client-id-mono">{c.clientId}</span></td>
                      <td>
                        {c.scopes.length === 0 ? (
                            <span style={{ color: '#8892a4', fontSize: 12 }}>none</span>
                        ) : (
                            c.scopes.map((s) => (
                                <span key={s} className="scope-tag">{s}</span>
                            ))
                        )}
                      </td>
                      <td>
                    <span className={c.isActive ? 'badge-active' : 'badge-inactive'}>
                      {c.isActive ? 'Active' : 'Inactive'}
                    </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#5b6b8a' }}>{formatDate(c.createdAt)}</td>
                      <td>
                        <div className="d-flex" style={{ gap: 6 }}>
                          <button
                              className="btn-action btn-action-edit"
                              onClick={() => openEdit(c)}
                          >
                            Edit
                          </button>
                          <button
                              className="btn-action btn-action-rotate"
                              onClick={() => handleRotateSecret(c)}
                          >
                            Rotate
                          </button>
                          <button
                            className={"btn-action " + (c.isActive ? "btn-action-deactivate" : "btn-action-activate")}
                            onClick={() => handleToggleActive(c)}
                          >
                            {c.isActive ? 'Désactiver' : 'Activer'}
                          </button>
                          {deleteConfirm === c.id ? (
                            <>
                              <button
                                  className="btn-action btn-action-delete"
                                  onClick={() => handleDelete(c.id)}
                              >
                                Confirm
                              </button>
                              <button
                                  className="btn-action"
                                  style={{ color: '#5b6b8a', borderColor: '#d0d5de' }}
                                  onClick={() => setDeleteConfirm(null)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                              <button
                                  className="btn-action btn-action-delete"
                                  onClick={() => setDeleteConfirm(c.id)}
                              >
                                Delete
                              </button>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
                </tbody>
              </table>
          )}
        </div>

        <ClientModal
            isOpen={clientModal}
            toggle={() => setClientModal(false)}
            client={editingClient}
            allScopes={allScopes}
            onSave={handleSave}
        />

        <SecretModal
            isOpen={secretModal}
            toggle={() => setSecretModal(false)}
            clientName={secretInfo.name}
            secret={secretInfo.secret}
        />
      </div>
  );
}
